import { FinancialPeriodPreparationMethod, type ISODate, PeriodType } from "@inrev/common";
import { DateTime } from "luxon";

export type PeriodSelectionFields = {
	periodEndDate?: ISODate;
	periodType?: PeriodType;
	preparationMethod?: FinancialPeriodPreparationMethod;
};

const latestByPeriodEndDate = <T extends PeriodSelectionFields>(candidates: T[]): T | null =>
	candidates.reduce<T | null>((best, item) => {
		if (!item.periodEndDate) return best;
		if (!best?.periodEndDate) return item;
		return DateTime.fromISO(item.periodEndDate) > DateTime.fromISO(best.periodEndDate)
			? item
			: best;
	}, null);

/**
 * Default period selection for financial statement grids: highest-preparation-quality
 * FYE per period end date, plus the latest interim when it is newer than the latest FYE.
 */
export const computeHighestQualityFyeDefaultPeriods = <T extends PeriodSelectionFields>(
	candidates: T[],
): T[] => {
	const fyeItems = candidates.filter((item) => item.periodType === PeriodType.Fye);
	const bestByDate = new Map<string, T>();
	for (const item of fyeItems) {
		if (!item.periodEndDate) continue;
		const existing = bestByDate.get(item.periodEndDate);
		if (
			!existing ||
			FinancialPeriodPreparationMethod.qualityRank(item.preparationMethod) <
				FinancialPeriodPreparationMethod.qualityRank(existing.preparationMethod)
		) {
			bestByDate.set(item.periodEndDate, item);
		}
	}
	const deduplicatedFye = [...bestByDate.values()];

	const latestFye = latestByPeriodEndDate(deduplicatedFye);
	const latestInterim = latestByPeriodEndDate(
		candidates.filter((item) => item.periodType && item.periodType !== PeriodType.Fye),
	);

	const result = [...deduplicatedFye];
	if (
		latestInterim?.periodEndDate &&
		(!latestFye?.periodEndDate ||
			DateTime.fromISO(latestInterim.periodEndDate) > DateTime.fromISO(latestFye.periodEndDate))
	) {
		result.push(latestInterim);
	}

	return result;
};
